import {forwardRef,useRef,Ref} from 'react'

const L = console.log
// MERGE `k` and `s` properties - usually this is the one you want
const submerge = (...args) => {
    // IGNORE non-objects (kind of)
    args = args.filter(_ => _)
    const $k:string[] = []
    const $s = {}
    for (const {k} of args.reverse()) {
        $k.push(k||"")
    }
    for (const {s} of args.reverse()) {
        Object.assign($s,s||{})
    }
    return Object.assign({},...args.reverse(),{k: $k, s: $s})
}

// FALSE to NULL
const fz = _ => _ === false ? null : _ === true ? null : _

function style (props:TStyle={}) {
    const map = {
        w: 'width',
        wmin: 'minWidth',
        wmax: 'maxWidth',

        padl: 'paddingLeft',
        padt: 'paddingTop',

        h: 'height',
        hmin: 'minHeight',
        hmax: 'maxHeight',

        fs: 'fontSize',
        fw: 'fontWeight',
        lh: 'lineHeight',
        c: 'color',
        ts: 'textShadow',
        ti: 'textIndent',

        m: 'margin',
        mt: 'marginTop',
        mb: 'marginBottom',
        ml: 'marginLeft',
        mr: 'marginRight',

        bg: 'backgroundColor',
        z: 'zIndex',
        p: 'position',
        pt: 'top',
        pl: 'left',
        o: 'opacity',
        b: 'border',
        br: 'borderRadius',
        bc: 'borderColor',
        tx: 'transition',
        ls: 'letterSpacing',
        us: 'userSelect',
    }

    const $style =
        Object.entries(props)
        .filter(([_,v]) => fz(v) != null)
        .reduce((p,[k,v]) => {
            if (map[k]) {
                p[map[k]] = k === 'lh' ? `${v}px` : v
            }
            else {
                p[k] = v
            }
            return p
        },{})
    // HACK: sub-pixel rendering background bleed
    if (props['bg'] && !$style['boxShadow']) $style['boxShadow'] = `0 0 0 0.1px ${props['bg']}`
    return $style
}

// JOIN/FILTER class names
function klass (...args:TKlass[]) : string | undefined {
    // reverse: first class properties win amongst conflicts
    const _ = args
        .filter(_ => _)
        .reverse()
        .reduce((p:string[],k) => { 
            // RECURSIVELY resolve arrays
            const kk = Array.isArray(k) ? klass(...(k as TKlass[])) : k
            if (kk) p.push(kk)
            return p
        },[])
        .join(" ")
    return _ ? _ : undefined
}

const Div = forwardRef(($props:any,ref:Ref<HTMLDivElement>) => {
    const localRef = useRef<HTMLDivElement>(null)
    const targetRef = ref || localRef
    const props = {...$props}
    if (props.k) {
        props.className = klass(props.k)
        delete props.k
    }
    if (props.s) {
        props.style = style(props.s)
        delete props.s
    }

    // CAPTURE child content
    const child = props.el || props.child || props.children

    // SWEEP OUT child content properties
    delete props.el
    delete props.child
    delete props.children

    return !child
        ? <div ref={targetRef} {...props}/>
        : <div ref={targetRef} {...props}>
            {child}
        </div>
})

export function Span ($props:any) {
    const props = {...$props}
    if (props.k) {
        props.className = klass(props.k)
        delete props.k
    }
    if (props.s) {
        props.style = style(props.s)
        delete props.s
    }

    return <span
        {...props}>
            {props.el || props.child || props.children}
    </span>
}

export type TKlass = string|false|undefined|TKlass[]

/*
 * on-click handler utility
 */
export function onClick (handler:(()=>void)|((e:MouseEvent)=>void)|undefined) {
    return {
        onClick: (e:MouseEvent) => {
            e.button === 0 && handler && handler(e)
            e.stopPropagation()
        }
    }
}

/**
 * ADJUST saturation of hex-encoded RGB color
 * @param {string} color - color, format "#RRGGBB"
 * @param {number} sat - 0 (grayscale) to 1 (full color)
 * @returns {string} - hex-encoded RGB value after saturation applied
 */
export function applySat (color:string, sat:number) {
    // EXTRACT RGB components
    color = color.trim()
    const r = parseInt(color.substring(1, 3), 16)
    const g = parseInt(color.substring(3, 5), 16)
    const b = parseInt(color.substring(5, 7), 16)
    
    // CALC luminance
    const lum = 0.299 * r + 0.587 * g + 0.114 * b
    // APPLY saturation
    // (CLAMP values to RGB range (0-255))
    const applySat = _ => Math.min(Math.round(lum + (_ - lum) * sat),0xff)
    // HEX-ENCODE
    const toHex = _ => _ < 0x10 ? '0' + _.toString(16) : _.toString(16)
    return '#' + toHex(applySat(r)) + toHex(applySat(g)) + toHex(applySat(b))
}
  
/**
 * ADJUST luminance of hex-encoded RGB color
 * @param {string} color - color, format "#RRGGBB"
 * @param {number} lum - luminance factor
 * @returns {string} - hex-encoded RGB value after luminance applied
 */
export function applyLum (color:string, lum:number) {
    // EXTRACT RGB components
    color = color.trim()
    const r = parseInt(color.substring(1, 3), 16)
    const g = parseInt(color.substring(3, 5), 16)
    const b = parseInt(color.substring(5, 7), 16)
    
    // APPLY luminance (simple scaling)
    const adjust = v => Math.max(0, Math.min(Math.round(v * lum), 0xff))
    
    // HEX-ENCODE
    const toHex = v => v < 0x10 ? '0' + v.toString(16) : v.toString(16)
    
    return '#' + toHex(adjust(r)) + toHex(adjust(g)) + toHex(adjust(b))
}

export type TStyle = {}

export const TOAST_STYLE = {
    background: 'var(--black-07)',
    border: '1px solid rgba(255,255,255,0.12)',
    color: '#dbdcde',
    boxShadow: '0 4px 24px rgba(0,0,0,0.6)',
}

export { fz,klass,Div,style,submerge}
